import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
/**
 * The SAME three values the content sections ship (`custom-columns-new`, `video`, `testimonials`…), so one
 * vocabulary answers "where does this show" everywhere in the inspector — and the inspector's dictionary
 * already translates them (`opt.always-display` / `opt.desktop-hidden` / `opt.mobile-hidden`).
 */
const ACTION_VISIBILITY_OPTIONS = [
    { value: 'always-display', label: 'All devices' },
    { value: 'desktop-hidden', label: 'Mobile only' },
    { value: 'mobile-hidden', label: 'Desktop only' },
];
/**
 * header — the site header (a GLOBAL section placed on the 'header' template). A logo (image, with a
 * `storeName` text fallback when no logo is uploaded), a horizontal nav that PICKS a named menu (Shopify
 * model: defined once in dashboard → Navigation, referenced by handle via the `menu` field), and the
 * search / account / cart icon actions. The LAYOUT select arranges those three (logo-left + centred nav /
 * logo-left + left nav / logo-centred with the nav on a second row — the Shopify-classic header positions).
 * An optional ANNOUNCEMENT BAR sits above it. Appearance carries a colour SCHEME (the same `--aqx-scheme-*`
 * contract the content sections use), a line-separator toggle, stickiness and a transparent-over-hero
 * treatment. It does NOT use the universal "Section" padding/width band — a header is full-bleed chrome —
 * but its CONTENT follows the page container through its own `contentWidth` (same four values as a
 * section's `width`), so the logo starts where the page's content starts.
 *
 * MENU MODEL: the nav comes from the picked menu's items (`resolved.menus[handle]`); the legacy repeatable
 * `link` blocks are kept as a FALLBACK (used when no menu is picked / the handle doesn't resolve — which is
 * also what gives the builder a real nav preview). version 2→3.
 *
 * v3→v4 adds `drawerWidth` (the off-canvas mobile menu's width) — a defaulted field, so an existing header
 * hydrates to the new 320px default with no migration work.
 *
 * v4→v5 adds the PER-DEVICE visibility of the three action icons (`searchVisibility`, `accountVisibility`,
 * `cartVisibility`). Shopify only lets you hide an icon everywhere; the ask here was to hide search /
 * account on the DESKTOP bar while the mobile header keeps them. The `show*` checkboxes stay the master
 * on/off switch (every stored document and every theme preset carries them), the select only says WHERE —
 * with the same three values the content sections already use, so the seller meets one vocabulary.
 * Defaulted ⇒ no migration work.
 *
 * v5→v6 adds `contentWidth`. The bar used to pin its logo to the viewport edge while every section below it
 * centred its content in a 1024px container — on any wide screen the logo and the product image started at
 * two different x. Defaulted to `standard` (the sections' own default), so an existing header lines up
 * without a migration; `full` restores the historic edge-to-edge bar.
 *
 * v6→v7 adds `navTextSize` — the menu links' size. `brandTextSize` already sized the text logo; the LINKS
 * were the one piece of header text with no knob at all, pinned to `--aqx-theme-text-sm`. The default (14)
 * reproduces that token EXACTLY — the render emits `calc(<size>px * var(--aqx-theme-body-scale,1))`, so a
 * theme's type scale still multiplies the seller's number the way it multiplied the token. Defaulted ⇒ no
 * migration, and hydrating the default onto a stored header renders the identical pixel.
 *
 * v7→v8 — ONE HEADER, TWO APPEARANCES (founder, 2026-09-15: « transparent sur la home, blanc sur la page
 * produit »). `transparentOnHero` used to be a blind GLOBAL checkbox: on meant transparent on every page,
 * including the product page, where there is no image to float over. Worse, it only dropped the BACKGROUND —
 * the text kept the single `colorScheme`, so the white logo a hero needs turned the product page's header
 * dark instead of white. Transparency and colour are one setting, not two.
 *
 * So the checkbox now arms an appearance the header wears WHERE IT MAKES SENSE, decided automatically: the
 * page's first section is a full-bleed visual (`renderer/opensOnMedia.ts`) or it is not. Home opens on a
 * hero → the header floats. The product page opens on `main-product` → the normal bar. Nothing to tick per
 * page, and a hero added to a product page tomorrow just works.
 *
 * That appearance carries its OWN colours (`heroScheme`, default inverse = white text) and, by default,
 * FILLS BACK IN on scroll (`fillOnScroll`): past the visual the bar takes its normal background and its
 * normal scheme back, so a white menu never slides over white content. Both defaulted ⇒ no migration; a
 * stored header with the checkbox off is untouched by all of it.
 *
 * v8→v9 adds `overMediaScope` — WHICH PAGES may float at all (founder, same day: « dans la page collection
 * je vois un header transparent, on veut pas ; ça aussi on a les pages personnalisées qu'on va créer »).
 * "The page opens on a visual" answered the home and the product page correctly and the COLLECTION page
 * wrongly: every collection opens on a `collection-banner`, which is a title band, not a hero. That entry
 * is gone from the openers — but the deeper lesson is that a rule she cannot see is a rule she cannot
 * predict, and she has custom pages coming that nobody has built yet. So the scope is now written down:
 * the home alone (the default, and exactly what she asked for), the home plus custom pages, or anywhere
 * a page opens on a visual. The automatic rule still applies WITHIN that scope — both must agree.
 *
 * v9→v10 adds `heroLogo` (founder, same day: « pour le header transparent j'utilise un logo white, mais
 * pour l'autre un noir »). `heroScheme` recolours TEXT — a logo is an IMAGE, and no colour setting can turn
 * a black PNG white. So the floating appearance gets its own logo file. Empty (the default) = keep the one
 * logo everywhere, which is every store that never floats. Both images ship in the markup and swap on
 * `[data-aqx-hdr-filled]` in pure CSS — the storefront does not hydrate this section, so a JS `src` swap
 * would simply never run there.
 *
 * v10→v11 adds `iconColor` (founder, 2026-09-16: « je dois pouvoir changer la couleur de cette icône de
 * panier dans le header — aussi la recherche, et compte »). The three action icons — and the hamburger
 * next to them, which is the same bar furniture — were pinned to the scheme's foreground with no knob at
 * all: a header could pick its background and its text, never its icons. ONE colour for the four, because
 * a red cart beside a black search is a mistake, not a setting.
 *
 * It paints the RESTING bar. While the header floats over a visual, `heroScheme` still owns every colour
 * it wears (a chosen dark icon over a photograph is the unreadable state the two appearances exist to
 * prevent) — and it takes this colour back the moment the bar fills in on scroll. Empty (the default)
 * = the icons keep inheriting the scheme, so every stored header renders the identical pixel ⇒ no
 * migration.
 *
 * v11→v12 adds `custom` to `colorScheme`, with `customBackground` + `customText` (founder, 2026-09-18:
 * « pour le composant header ajoute la possibilité de faire couleur custom »). The bar could take the
 * theme's three palettes and nothing else, so a brand colour in the menu meant repainting the WHOLE
 * store's `accent1`. The two colours are the same pair every other band already offers — same field ids,
 * so they read in the seven dictionaries with no new key — and they override the `--aqx-scheme-*`
 * contract in place, which is why the drawer, the search panel and the icons follow the bar instead of
 * staying on the old palette.
 *
 * It dresses the RESTING bar, like `iconColor`: while the header floats over a visual, `heroScheme`
 * owns every colour, and the custom pair comes back with the rest of the normal bar when it fills in on
 * scroll. Default unchanged (`light`) and the fields only read when the select says `custom` ⇒ every
 * stored header renders the identical pixel, no migration.
 */
export const headerSchema = {
    type: 'header',
    name: 'Header',
    version: 12,
    templates: ['header'],
    fields: [
        // ── Announcement bar ──────────────────────────────────────────────────────
        {
            id: 'showAnnouncement',
            type: 'checkbox',
            label: 'Show announcement bar',
            group: 'Announcement bar',
            info: 'A thin bar above the header — promotions, shipping notice, etc.',
            default: true,
        },
        {
            id: 'announcementText',
            type: 'text',
            label: 'Announcement text',
            group: 'Announcement bar',
            default: 'Livraison offerte dès 400 MAD · Paiement à la livraison disponible',
            defaultKey: 'section.header_announcement',
            maxLength: 120,
            visibleWhen: { field: 'showAnnouncement', equals: true },
        },
        {
            id: 'announcementLink',
            type: 'link',
            label: 'Announcement link',
            group: 'Announcement bar',
            default: undefined,
            visibleWhen: { field: 'showAnnouncement', equals: true },
        },
        {
            id: 'announcementScheme',
            type: 'select',
            label: 'Announcement scheme',
            group: 'Announcement bar',
            default: 'accent',
            options: [
                { value: 'light', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'accent', label: 'Accent' },
            ],
            visibleWhen: { field: 'showAnnouncement', equals: true },
        },
        // ── Logo ────────────────────────────────────────────────────────────────
        {
            id: 'logo',
            type: 'image',
            label: 'Logo',
            group: 'Logo',
            info: 'Upload a logo. When empty, the store name shows as text.',
        },
        {
            id: 'heroLogo',
            type: 'image',
            label: 'Logo over a visual',
            group: 'Logo',
            info: 'The logo used while the bar floats over a visual — usually a white version of it. Leave it empty to keep the same logo everywhere. Colors cannot do this job: a color setting recolours text, never the pixels of an image.',
            visibleWhen: { field: 'transparentOnHero', equals: true },
        },
        {
            id: 'storeName',
            type: 'text',
            label: 'Store name (logo fallback)',
            group: 'Logo',
            // NO default, deliberately (23/09/2026). This used to read `default: 'aquilixShop'`, and
            // `applyDefaults` STORES what it fills — so every store that added a header, and every seller who
            // never opened this group, carried OUR product name as their brand: in their header, in their footer
            // heading, and on their checkout, which reads this very setting for its brand mark. Left empty, the
            // section falls back to the store's real name (see `brandName` in Component.tsx), which is the only
            // sane meaning of "store name". The seller types here only to show something OTHER than their name.
            maxLength: 40,
        },
        {
            id: 'logoWidth',
            type: 'range',
            label: 'Logo width',
            group: 'Logo',
            default: 120,
            min: 60,
            max: 240,
            step: 4,
            unit: 'px',
        },
        {
            id: 'brandTextSize',
            type: 'range',
            label: 'Store name size',
            group: 'Logo',
            info: 'Size of the text logo (when no logo image is uploaded).',
            default: 20,
            min: 14,
            max: 40,
            step: 1,
            unit: 'px',
        },
        // ── Menu ────────────────────────────────────────────────────────────────
        {
            id: 'menu',
            type: 'menu',
            label: 'Menu',
            group: 'Menu',
            info: 'Pick a menu you built in Navigation. Falls back to the manual links below if unset.',
            default: 'main-menu',
        },
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Logo & menu position',
            group: 'Layout',
            default: 'logo-left',
            options: [
                { value: 'logo-left', label: 'Logo left, menu centered' },
                { value: 'logo-left-nav-left', label: 'Logo left, menu left' },
                { value: 'logo-center', label: 'Logo centered, menu below' },
            ],
        },
        {
            id: 'navTextSize',
            type: 'range',
            label: 'Menu text size',
            group: 'Layout',
            info: 'Size of the menu links. The mobile menu drawer follows, two pixels larger (its rows are touch targets).',
            default: 14,
            min: 11,
            max: 24,
            step: 1,
            unit: 'px',
        },
        {
            id: 'navGap',
            type: 'range',
            label: 'Nav link spacing',
            group: 'Layout',
            default: 20,
            min: 12,
            max: 64,
            step: 4,
            unit: 'px',
        },
        {
            id: 'barPaddingY',
            type: 'range',
            label: 'Bar vertical padding',
            group: 'Layout',
            default: 16,
            min: 8,
            max: 40,
            step: 2,
            unit: 'px',
        },
        {
            id: 'barPaddingX',
            type: 'range',
            label: 'Bar horizontal padding',
            group: 'Layout',
            // Matches --aqx-theme-gutter so the untouched header aligns with the sections.
            default: 40,
            min: 16,
            max: 120,
            step: 4,
            unit: 'px',
        },
        {
            id: 'contentWidth',
            type: 'select',
            label: 'Content width',
            group: 'Layout',
            info: 'Where the logo and the menu start. Match it to the width your sections use and the logo lines up with the page content instead of hugging the screen edge. The bar itself stays full width.',
            default: 'standard',
            options: [
                { value: 'narrow', label: 'Narrow' },
                { value: 'standard', label: 'Standard' },
                { value: 'wide', label: 'Wide' },
                { value: 'full', label: 'Full width' },
            ],
        },
        {
            id: 'drawerWidth',
            type: 'range',
            label: 'Menu drawer width',
            group: 'Layout',
            info: 'Width of the off-canvas mobile menu. It never exceeds the screen (always leaves a 40px gap).',
            default: 320,
            min: 240,
            max: 480,
            step: 10,
            unit: 'px',
        },
        {
            id: 'mobileLogoPosition',
            type: 'select',
            label: 'Mobile logo position',
            group: 'Layout',
            info: 'Below 990px the bar collapses to hamburger + logo + icons. Center matches Shrine; left groups the hamburger and logo together.',
            default: 'center',
            options: [
                { value: 'center', label: 'Centered' },
                { value: 'left', label: 'Left (next to menu)' },
            ],
        },
        // ── Actions ─────────────────────────────────────────────────────────────
        {
            id: 'showSearch',
            type: 'checkbox',
            label: 'Show search',
            group: 'Actions',
            default: true,
        },
        {
            id: 'searchVisibility',
            type: 'select',
            label: 'Show search on',
            group: 'Actions',
            info: 'The bar switches to its mobile form below 990px. "Mobile only" drops the icon from the desktop bar and keeps it next to the hamburger.',
            default: 'always-display',
            options: ACTION_VISIBILITY_OPTIONS,
            visibleWhen: { field: 'showSearch', equals: true },
        },
        {
            id: 'showAccount',
            type: 'checkbox',
            label: 'Show account',
            group: 'Actions',
            default: true,
        },
        {
            id: 'accountVisibility',
            type: 'select',
            label: 'Show account on',
            group: 'Actions',
            info: 'The bar switches to its mobile form below 990px. "Desktop only" also drops the account link from the menu drawer, which is mobile-only.',
            default: 'always-display',
            options: ACTION_VISIBILITY_OPTIONS,
            visibleWhen: { field: 'showAccount', equals: true },
        },
        {
            id: 'showCart',
            type: 'checkbox',
            label: 'Show cart',
            group: 'Actions',
            default: true,
        },
        {
            id: 'cartVisibility',
            type: 'select',
            label: 'Show cart on',
            group: 'Actions',
            info: 'The bar switches to its mobile form below 990px.',
            default: 'always-display',
            options: ACTION_VISIBILITY_OPTIONS,
            visibleWhen: { field: 'showCart', equals: true },
        },
        {
            id: 'iconColor',
            type: 'color',
            label: 'Icons color',
            group: 'Actions',
            info: 'The colour of the search, account and cart icons — and of the mobile menu button, which sits on the same bar. Leave it empty and they follow the color scheme, as they always have. It does not apply while the bar floats over a visual: there the "Colors over a visual" setting owns every colour, and this one comes back as soon as the bar fills in.',
        },
        // ── Appearance ──────────────────────────────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Appearance',
            default: 'light',
            options: [
                { value: 'light', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'accent', label: 'Accent' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // The bar's own two colours, read ONLY when the scheme above says `custom`. Same ids as the sections'
        // universal pair on purpose: `field.customBackground` / `field.customText` are already translated in
        // every delivered dictionary, so this ships in the seven languages without a new key.
        // `equals`, never `notEquals`: a stored header has no `colorScheme` key at all when it was never
        // touched, and `notEquals` is TRUE on an absent value — it would open two colour pickers that move
        // nothing on every untouched header in the fleet.
        {
            id: 'customBackground',
            type: 'color',
            label: 'Background',
            group: 'Appearance',
            default: { ref: 'custom', value: '#1E3A54' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Text',
            group: 'Appearance',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
            info: 'Paints the menu, the store name and the icons, and the line under the bar is derived from it. While the header floats over a visual, "Colors over a visual" owns every colour instead — this pair comes back when the bar fills in on scroll.',
        },
        {
            id: 'showLineSeparator',
            type: 'checkbox',
            label: 'Show bottom line',
            group: 'Appearance',
            default: true,
        },
        {
            id: 'sticky',
            type: 'checkbox',
            label: 'Sticky on scroll',
            group: 'Appearance',
            default: true,
        },
        {
            id: 'transparentOnHero',
            type: 'checkbox',
            label: 'Float over full-bleed visuals',
            group: 'Appearance',
            info: 'On pages that OPEN with a full-bleed visual (hero, slideshow, banner…), the bar lays itself over the image instead of sitting above it. Pages that open on anything else — a product page, for instance — keep the normal bar. Nothing to set page by page.',
            default: false,
        },
        {
            id: 'overMediaScope',
            type: 'select',
            label: 'Float on',
            group: 'Appearance',
            info: 'Which pages may let the bar float. Inside them it still only happens when the page actually opens with a full-bleed visual — a product or collection page is never affected.',
            default: 'home-only',
            options: [
                { value: 'home-only', label: 'Home page only' },
                { value: 'home-and-pages', label: 'Home page and custom pages' },
                { value: 'any-visual', label: 'Any page that opens with a visual' },
            ],
            visibleWhen: { field: 'transparentOnHero', equals: true },
        },
        {
            id: 'heroScheme',
            type: 'select',
            label: 'Colors over a visual',
            group: 'Appearance',
            info: 'The colors the bar wears while it floats. Inverse paints the logo and menu in your Background 1 color — white on a light theme, which is what most photographs need. It never applies to the normal bar.',
            default: 'inverse',
            options: [
                { value: 'light', label: 'Background 1' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'accent', label: 'Accent' },
            ],
            visibleWhen: { field: 'transparentOnHero', equals: true },
        },
        {
            id: 'fillOnScroll',
            type: 'checkbox',
            label: 'Fill in on scroll',
            group: 'Appearance',
            info: 'Once the visual has scrolled past, the bar takes its normal background and colors back and stays at the top. Leave it on — a white menu over white content is unreadable.',
            default: true,
            visibleWhen: { field: 'transparentOnHero', equals: true },
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18).
        ...animationFields,
    ],
    blocks: [
        {
            type: 'link',
            name: 'Nav link (fallback)',
            max: 8,
            fields: [
                { id: 'label', type: 'text', label: 'Label', default: 'Lien', maxLength: 32 },
                { id: 'url', type: 'link', label: 'Link', default: undefined },
            ],
        },
    ],
    maxBlocks: 8,
    migrate(fromVersion, settings) {
        // v2→v3: add the `menu` picker. Existing headers keep their `link` blocks (the fallback nav), so a
        // store with no `menu` set renders identically; the field hydrates to its default.
        // v3→v4: add `drawerWidth`. v4→v5: add the three `*Visibility` selects. v5→v6: `contentWidth`.
        // v6→v7: `navTextSize`, whose default re-emits the token it replaced. v7→v8: `heroScheme` +
        // `fillOnScroll`, v8→v9: `overMediaScope`, v9→v10: `heroLogo` — all four only bite when
        // `transparentOnHero` is on, and `heroLogo` has no default at all. v10→v11: `iconColor`, which has
        // no default either — empty means "inherit the scheme", the behaviour every stored header has.
        // All defaulted ⇒ passthrough
        // (and passthrough is what keeps this idempotent: a stored instance is never re-stamped, so migrate
        // runs again on every render of an already-edited header).
        if (fromVersion >= 5)
            return settings;
        return settings;
    },
};
export const headerPresets = [
    {
        name: 'Header',
        settings: {
            // The announcement bar is now its OWN dedicated `announcement-bar` section (selectable + stylable);
            // keep the header's built-in bar OFF by default so new pages don't show two bars.
            showAnnouncement: false,
            announcementScheme: 'accent',
            // No `storeName` either — same reason as the field above: the preset is what stamps a brand into a
            // brand-new header, and the only right brand to stamp is the shop's own, which the render resolves.
            menu: 'main-menu',
            logoWidth: 120,
            layout: 'logo-left',
            contentWidth: 'standard',
            drawerWidth: 320,
            showSearch: true,
            searchVisibility: 'always-display',
            showAccount: true,
            accountVisibility: 'always-display',
            showCart: true,
            cartVisibility: 'always-display',
            colorScheme: 'light',
            showLineSeparator: true,
            sticky: true,
            transparentOnHero: false,
            overMediaScope: 'home-only',
            heroScheme: 'inverse',
            fillOnScroll: true,
        },
        blocks: [
            { type: 'link', settings: { label: 'Boutique', url: { type: 'collection', slug: 'all' } } },
            { type: 'link', settings: { label: 'Nouveautés', url: { type: 'collection', slug: 'new' } } },
            { type: 'link', settings: { label: 'Contact', url: { type: 'page', slug: 'contact' } } },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const headerMeta = {
    label: 'Header',
    group: 'Header',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map